<script setup>
import { ref, computed } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useWhatsappEmbeddedSignup } from 'dashboard/composables/useWhatsappEmbeddedSignup';
import Icon from 'next/icon/Icon.vue';
import NextButton from 'next/button/Button.vue';
import Banner from 'next/banner/Banner.vue';
import LoadingState from 'dashboard/components/widgets/LoadingState.vue';
import InboxesAPI from 'dashboard/api/inboxes';
import { parseAPIErrorResponse } from 'dashboard/store/utils/api';

const props = defineProps({
  enableCallingOnComplete: {
    type: Boolean,
    default: false,
  },
  isDisabled: {
    type: Boolean,
    default: false,
  },
  showRestrictionAlert: {
    type: Boolean,
    default: false,
  },
  restrictionStatusUrl: {
    type: String,
    default: '',
  },
});

const store = useStore();
const router = useRouter();
const { t } = useI18n();
const { isAuthenticating, runEmbeddedSignup } = useWhatsappEmbeddedSignup();

const isProcessing = ref(false);
const processingMessage = ref('');

const steps = [
  {
    title: 'Entre na sua conta Meta',
    description:
      'Use uma conta que tenha permissão para administrar o WhatsApp da sua empresa.',
    icon: 'i-hugeicons-user-circle',
  },
  {
    title: 'Escolha a empresa e o número',
    description:
      'Siga as orientações da Meta para selecionar ou cadastrar os dados do seu negócio.',
    icon: 'i-hugeicons-store-01',
  },
  {
    title: 'Confirme e volte para a Talkvia',
    description:
      'Revise as permissões. Ao concluir, preparamos a conexão para o atendimento.',
    icon: 'i-hugeicons-checkmark-circle-02',
  },
];

const requirements = [
  {
    icon: 'i-hugeicons-user-circle',
    text: 'Acesso de administrador ao Facebook da empresa',
  },
  {
    icon: 'i-hugeicons-smart-phone-01',
    text: 'O número de telefone que vai atender os clientes',
  },
];

const coexistenceTips = [
  'Tenha o celular com o WhatsApp Business por perto: a Meta pode pedir a confirmação pelo aplicativo.',
  'Quando o celular perguntar, aceite compartilhar o histórico. Assim trazemos as conversas de até 6 meses e seus contatos.',
  'Fotos, áudios e arquivos antigos não são importados; as mensagens novas chegam completas.',
];

const showLoader = computed(() => isAuthenticating.value || isProcessing.value);

const enableCallingForInbox = async inboxId => {
  try {
    await InboxesAPI.enableWhatsappCalling(inboxId);
  } catch (_) {
    useAlert(
      t('INBOX_MGMT.ADD.WHATSAPP.EMBEDDED_SIGNUP.CALLING_ENABLE_FAILED')
    );
  }
};

const handleSignupSuccess = async inboxData => {
  if (inboxData && inboxData.id) {
    if (props.enableCallingOnComplete) {
      isProcessing.value = true;
      processingMessage.value = t(
        'INBOX_MGMT.ADD.WHATSAPP.EMBEDDED_SIGNUP.ENABLING_CALLING'
      );
      await enableCallingForInbox(inboxData.id);
    }
    isProcessing.value = false;
    isAuthenticating.value = false;
    useAlert(t('INBOX_MGMT.FINISH.MESSAGE'));
    router.replace({
      name: 'settings_inboxes_add_agents',
      params: {
        page: 'new',
        inbox_id: inboxData.id,
      },
    });
  } else {
    isProcessing.value = false;
    isAuthenticating.value = false;
    useAlert(t('INBOX_MGMT.ADD.WHATSAPP.EMBEDDED_SIGNUP.SUCCESS_FALLBACK'));
    router.replace({
      name: 'settings_inbox_list',
    });
  }
};

const launchEmbeddedSignup = async () => {
  if (props.isDisabled) return;

  let credentials;
  try {
    credentials = await runEmbeddedSignup();
  } catch (error) {
    useAlert(
      error.message ||
        t('INBOX_MGMT.ADD.WHATSAPP.EMBEDDED_SIGNUP.SDK_LOAD_ERROR')
    );
    return;
  }

  // Resolves null when the user dismisses the Meta popup.
  if (!credentials) {
    useAlert(t('INBOX_MGMT.ADD.WHATSAPP.EMBEDDED_SIGNUP.CANCELLED'));
    return;
  }

  isProcessing.value = true;
  processingMessage.value = t(
    'INBOX_MGMT.ADD.WHATSAPP.EMBEDDED_SIGNUP.PROCESSING'
  );
  try {
    const inboxData = await store.dispatch(
      'inboxes/createWhatsAppEmbeddedSignup',
      credentials
    );
    await handleSignupSuccess(inboxData);
  } catch (error) {
    isProcessing.value = false;
    useAlert(
      parseAPIErrorResponse(error) ||
        t('INBOX_MGMT.ADD.WHATSAPP.API.ERROR_MESSAGE')
    );
  }
};
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <div class="h-full">
    <LoadingState v-if="showLoader" :message="processingMessage" />

    <div v-else class="flex flex-col gap-6">
      <header class="flex flex-col gap-4 sm:flex-row sm:items-start">
        <span
          class="grid size-14 shrink-0 place-items-center rounded-2xl bg-[#18804D] text-white shadow-sm"
        >
          <Icon icon="i-brand-whatsapp" class="size-7" />
        </span>
        <div class="min-w-0">
          <span
            class="inline-flex items-center gap-1.5 rounded-full bg-n-teal-3 px-2.5 py-1 text-xs font-semibold text-n-teal-11"
          >
            <Icon icon="i-hugeicons-checkmark-circle-02" class="size-3.5" />
            Conexão oficial da Meta
          </span>
          <h3
            class="mb-0 mt-2.5 text-2xl font-semibold tracking-tight text-n-slate-12"
          >
            Conecte seu WhatsApp à Talkvia
          </h3>
          <p class="mb-0 mt-1.5 text-sm leading-6 text-n-slate-11">
            Tudo acontece numa janela oficial da Meta. Você acompanha cada etapa
            e escolhe o que autorizar.
          </p>
        </div>
      </header>

      <section
        class="rounded-2xl bg-n-alpha-1 p-4"
        aria-labelledby="whatsapp-signup-requirements"
      >
        <h4
          id="whatsapp-signup-requirements"
          class="mb-3 text-sm font-semibold text-n-slate-12"
        >
          Antes de começar, tenha em mãos
        </h4>
        <ul class="!m-0 grid list-none gap-2.5 p-0 sm:grid-cols-2">
          <li
            v-for="item in requirements"
            :key="item.text"
            class="flex items-center gap-2.5 text-sm text-n-slate-12"
          >
            <span
              class="grid size-8 shrink-0 place-items-center rounded-lg bg-n-solid-1 text-n-slate-11 shadow-sm"
            >
              <Icon :icon="item.icon" class="size-4" />
            </span>
            {{ item.text }}
          </li>
        </ul>
      </section>

      <section aria-labelledby="whatsapp-signup-steps">
        <h4
          id="whatsapp-signup-steps"
          class="mb-3 text-sm font-semibold text-n-slate-12"
        >
          Como funciona
        </h4>
        <ol class="!m-0 grid list-none gap-4 p-0 md:grid-cols-3 md:gap-3">
          <li
            v-for="(step, index) in steps"
            :key="step.title"
            class="relative flex gap-3 md:flex-col md:gap-2.5"
          >
            <span
              v-if="index < steps.length - 1"
              class="absolute left-[17px] top-10 h-[calc(100%-1rem)] w-px bg-n-weak md:left-12 md:right-0 md:top-[17px] md:h-px md:w-auto"
              aria-hidden="true"
            />
            <span
              class="relative grid size-9 shrink-0 place-items-center rounded-full bg-n-brand/10 text-sm font-semibold text-n-brand ring-4 ring-n-solid-1"
            >
              {{ index + 1 }}
            </span>
            <div class="min-w-0 md:pr-3">
              <p class="mb-0.5 text-sm font-semibold text-n-slate-12">
                {{ step.title }}
              </p>
              <p class="mb-0 text-[13px] leading-5 text-n-slate-11">
                {{ step.description }}
              </p>
            </div>
          </li>
        </ol>
      </section>

      <section
        class="rounded-2xl border border-n-teal-6 bg-n-teal-2 p-4"
        aria-labelledby="whatsapp-signup-coexistence"
      >
        <div class="flex items-start gap-3">
          <span
            class="grid size-9 shrink-0 place-items-center rounded-xl bg-n-solid-1 text-n-teal-11 shadow-sm"
          >
            <Icon icon="i-hugeicons-message-multiple-01" class="size-[18px]" />
          </span>
          <div class="min-w-0">
            <h4
              id="whatsapp-signup-coexistence"
              class="mb-1 text-sm font-semibold text-n-slate-12"
            >
              Já usa o WhatsApp Business no celular?
            </h4>
            <p class="mb-0 text-sm leading-6 text-n-slate-11">
              Quando a Meta oferecer essa opção para o seu número, você continua
              usando o aplicativo e a Talkvia ao mesmo tempo.
            </p>
            <ul class="!mb-0 !ml-0 mt-3 grid list-none gap-2 p-0">
              <li
                v-for="tip in coexistenceTips"
                :key="tip"
                class="flex items-start gap-2 text-[13px] leading-5 text-n-slate-12"
              >
                <Icon
                  icon="i-hugeicons-checkmark-circle-02"
                  class="mt-0.5 size-4 shrink-0 text-n-teal-11"
                />
                {{ tip }}
              </li>
            </ul>
          </div>
        </div>
      </section>

      <Banner v-if="showRestrictionAlert" color="amber" class="w-full">
        <div class="flex items-start gap-3 text-start">
          <Icon
            icon="i-lucide-triangle-alert"
            class="flex-shrink-0 size-4 mt-0.5"
          />
          <span>
            {{
              $t('INBOX_MGMT.ADD.WHATSAPP.EMBEDDED_SIGNUP.RESTRICTED_WARNING')
            }}
            <a
              v-if="restrictionStatusUrl"
              :href="restrictionStatusUrl"
              class="link underline"
              rel="noopener noreferrer nofollow"
              target="_blank"
            >
              {{ $t('INBOX_MGMT.ADD.WHATSAPP.EMBEDDED_SIGNUP.STATUS_LINK') }}
            </a>
          </span>
        </div>
      </Banner>

      <div class="flex flex-col items-center gap-3">
        <NextButton
          :disabled="isAuthenticating || isDisabled"
          :is-loading="isAuthenticating"
          icon="i-brand-whatsapp"
          solid
          blue
          lg
          class="w-full"
          @click="launchEmbeddedSignup"
        >
          Conectar meu WhatsApp
        </NextButton>
        <p
          class="mb-0 flex items-center gap-1.5 text-center text-xs text-n-slate-11"
        >
          <Icon icon="i-hugeicons-lock" class="size-3.5 shrink-0" />
          Uma janela da Meta será aberta. A Talkvia não recebe sua senha.
        </p>
        <a
          href="/hc/manual/pt_BR/conexoes-e-canais"
          target="_blank"
          rel="noopener noreferrer"
          class="inline-flex items-center gap-1 text-sm font-medium text-n-brand hover:underline"
        >
          Precisa de ajuda? Consulte o manual da Talkvia
          <Icon icon="i-hugeicons-arrow-right-01" class="size-4" />
        </a>
      </div>
    </div>
  </div>
</template>
